import { CreditCard, Loader2, Plus, Star, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import {
  usePaymentMethods,
  useAddCardSession,
  useSetDefaultCard,
  useRemoveCard
} from "@/hooks/usePayments";

/**
 * Saved cards. The card itself lives in Stripe's vault (never our DB); we only
 * show brand / last-4 / expiry. Adding a card redirects to a Stripe setup page
 * (no charge); once saved it's reused for one-click purchases and renewals.
 */
export function PaymentMethodsCard() {
  const { data: cards, isLoading } = usePaymentMethods();
  const addCard = useAddCardSession();
  const setDefault = useSetDefaultCard();
  const removeCard = useRemoveCard();
  const { toast } = useToast();

  async function onAddCard() {
    try {
      const res = await addCard.mutateAsync();
      if (res.setup_url) window.location.href = res.setup_url;
    } catch (err) {
      toast({
        title: "Couldn't open the card form",
        description: err instanceof ApiError ? err.message : "Please try again.",
        variant: "destructive"
      });
    }
  }

  async function onDefault(id: string) {
    try {
      await setDefault.mutateAsync(id);
      toast({ title: "Default card updated", variant: "success" });
    } catch {
      toast({ title: "Couldn't update default card", variant: "destructive" });
    }
  }

  async function onRemove(id: string) {
    try {
      await removeCard.mutateAsync(id);
      toast({ title: "Card removed", variant: "success" });
    } catch {
      toast({ title: "Couldn't remove card", variant: "destructive" });
    }
  }

  const list = cards ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-4 w-4 text-primary" />
            Payment methods
          </CardTitle>
          <CardDescription>
            Saved cards are stored securely by Stripe and reused for purchases and renewals —
            you won't need to re-enter them.
          </CardDescription>
        </div>
        <Button size="sm" onClick={onAddCard} disabled={addCard.isPending} className="shrink-0">
          {addCard.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add card
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No saved cards yet. Add one so future purchases and renewals are one click.
          </p>
        ) : (
          <div className="space-y-2">
            {list.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="capitalize font-medium">{c.brand}</span>
                  <span className="text-muted-foreground">•••• {c.last4}</span>
                  <span className="text-xs text-muted-foreground">
                    exp {String(c.expMonth).padStart(2, "0")}/{String(c.expYear).slice(-2)}
                  </span>
                  {c.isDefault && <Badge variant="success">Default</Badge>}
                </div>
                <div className="flex items-center gap-1">
                  {!c.isDefault && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDefault(c.id)}
                      disabled={setDefault.isPending}
                      title="Make default"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRemove(c.id)}
                    disabled={removeCard.isPending}
                    title="Remove card"
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
